import React from "react";
import { Box } from "@mui/material";
import AgentAvatar from "./AgentAvatar";

const COMPACT_MAX_VISIBLE = 3;

function compactOverlap(size) {
  return Math.max(8, Math.round(size * 0.38));
}

function AvatarStackCell({
  agent,
  index,
  stackSize,
  size,
  overlap,
  borderRadius,
  fontSize,
  borderColor,
  direction = "horizontal",
}) {
  const isVertical = direction === "vertical";

  return (
    <Box
      sx={{
        ...(index > 0
          ? isVertical
            ? { mt: `-${overlap}px` }
            : { ml: `-${overlap}px` }
          : {}),
        zIndex: stackSize - index,
        borderRadius: "5px",
        border: "1.5px solid",
        borderColor,
        lineHeight: 0,
        flexShrink: 0,
      }}
    >
      <AgentAvatar
        name={agent.name}
        src={agent.avatarUrl}
        size={size}
        borderRadius={borderRadius}
        fontSize={fontSize}
        systemAgent={agent.systemAgent}
      />
    </Box>
  );
}

function OverflowStackCell({
  count,
  size,
  overlap,
  borderRadius,
  borderColor,
  direction = "horizontal",
}) {
  const isVertical = direction === "vertical";

  return (
    <Box
      sx={{
        ...(isVertical ? { mt: `-${overlap}px` } : { ml: `-${overlap}px` }),
        width: size,
        height: size,
        borderRadius,
        border: "1.5px solid",
        borderColor,
        backgroundColor: "action.selected",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: Math.max(8, size * 0.45),
        fontWeight: 600,
        color: "text.secondary",
        flexShrink: 0,
        zIndex: 0,
      }}
    >
      +{count}
    </Box>
  );
}

/**
 * Up to 3 overlapping avatars, then +N.
 * Horizontal by default; vertical keeps a fixed narrow column in the sessions pane.
 */
function CompactStackedAvatars({
  list,
  size,
  borderRadius,
  fontSize,
  borderColor,
  direction = "horizontal",
}) {
  const overlap = compactOverlap(size);
  const visible = list.slice(0, COMPACT_MAX_VISIBLE);
  const extra = list.length - visible.length;
  const isVertical = direction === "vertical";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isVertical ? "column" : "row",
        alignItems: isVertical ? "flex-start" : "center",
        flexShrink: 0,
        width: isVertical ? size : undefined,
        height: isVertical ? undefined : size,
        minWidth: 0,
      }}
    >
      {visible.map((agent, index) => (
        <AvatarStackCell
          key={agent.agentId || `${agent.name}-${index}`}
          agent={agent}
          index={index}
          stackSize={visible.length + (extra > 0 ? 1 : 0)}
          size={size}
          overlap={overlap}
          borderRadius={borderRadius}
          fontSize={fontSize}
          borderColor={borderColor}
          direction={direction}
        />
      ))}
      {extra > 0 && (
        <OverflowStackCell
          count={extra}
          size={size}
          overlap={overlap}
          borderRadius={borderRadius}
          borderColor={borderColor}
          direction={direction}
        />
      )}
    </Box>
  );
}

/**
 * Overlapping agent avatars for team rows (sidebar team list, session list, welcome composer).
 *
 * layout="row"              — horizontal overlap (more space available)
 * layout="compact"            — up to 3 tight overlapping avatars, then +N
 * layout="compact-vertical"   — same as compact, stacked vertically (sessions pane)
 */
export default function StackedAgentAvatars({
  agents = [],
  size = 20,
  maxVisible = 4,
  borderRadius = "4px",
  fontSize = "10px",
  selected = false,
  layout = "row",
}) {
  const list = Array.isArray(agents) ? agents.filter(Boolean) : [];
  if (list.length === 0) return null;

  const borderColor = selected ? "action.hover" : "background.subtle";

  if (layout === "compact" || layout === "compact-vertical") {
    return (
      <CompactStackedAvatars
        list={list}
        size={size}
        borderRadius={borderRadius}
        fontSize={fontSize}
        borderColor={borderColor}
        direction={layout === "compact-vertical" ? "vertical" : "horizontal"}
      />
    );
  }

  const visible = list.slice(0, maxVisible);
  const extra = list.length - visible.length;
  const overlap = 6;

  return (
    <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
      {visible.map((agent, index) => (
        <AvatarStackCell
          key={agent.agentId || `${agent.name}-${index}`}
          agent={agent}
          index={index}
          stackSize={visible.length + (extra > 0 ? 1 : 0)}
          size={size}
          overlap={overlap}
          borderRadius={borderRadius}
          fontSize={fontSize}
          borderColor={borderColor}
        />
      ))}
      {extra > 0 && (
        <OverflowStackCell
          count={extra}
          size={size}
          overlap={overlap}
          borderRadius={borderRadius}
          borderColor={borderColor}
        />
      )}
    </Box>
  );
}
